import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useGetBusiness } from '@marketing-milk/frontend'
import {
  ClientSentiment,
  ClientSentimentPatch,
  SprintPacketHydrated,
} from '@marketing-milk/interfaces'
import { useUpdateSprintPacket } from 'hooks/api/useSprintPackets'

const emptySentiment = (): ClientSentiment => ({
  likedToday: null,
  competitorsPosition: null,
  competitorsNotes: null,
  wouldRenew: null,
  wouldRenewNotes: null,
  wouldRecommend: null,
  wouldRecommendNotes: null,
  updatedAt: new Date(0).toISOString(),
})

export const useClientSentimentSave = (
  packetID: number,
  hydrated: ClientSentiment | null | undefined
) => {
  const hydratedValue = hydrated ?? emptySentiment()
  const [draft, setDraft] = useState<ClientSentiment>(hydratedValue)
  const lastSavedRef = useRef<ClientSentiment>(hydratedValue)
  const saveSeqRef = useRef(0)
  const fieldChainsRef = useRef<Partial<Record<keyof ClientSentiment, Promise<unknown>>>>({})

  const sameAsSaved = (a: ClientSentiment, b: ClientSentiment) =>
    a.likedToday === b.likedToday &&
    a.competitorsPosition === b.competitorsPosition &&
    a.competitorsNotes === b.competitorsNotes &&
    a.wouldRenew === b.wouldRenew &&
    a.wouldRenewNotes === b.wouldRenewNotes &&
    a.wouldRecommend === b.wouldRecommend &&
    a.wouldRecommendNotes === b.wouldRecommendNotes

  useEffect(() => {
    const next = hydrated ?? emptySentiment()
    setDraft(prev => (sameAsSaved(prev, lastSavedRef.current) ? next : prev))
    lastSavedRef.current = next
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated?.updatedAt])

  const update = useUpdateSprintPacket(packetID)
  const queryClient = useQueryClient()
  const business = useGetBusiness()
  const packetQueryKey = ['sprint-packet', business.id, packetID]

  const saveField = <K extends keyof ClientSentiment>(field: K, value: ClientSentiment[K]) => {
    const updatedAt = new Date().toISOString()
    const next: ClientSentiment = { ...draft, [field]: value, updatedAt }
    setDraft(next)
    if (sameAsSaved(next, lastSavedRef.current)) return

    const cached = queryClient.getQueryData<SprintPacketHydrated>(packetQueryKey)
    if (cached) {
      queryClient.setQueryData<SprintPacketHydrated>(packetQueryKey, {
        ...cached,
        clientSentiment: next,
      })
    }
    const seq = ++saveSeqRef.current
    // Send only the changed field; server merges field-by-field so an
    // out-of-order arrival for a different field can't overwrite this one.
    // Same-field saves chain per-field so rapid clicks (no→yes) reach the
    // server in click order — the server has no per-field timestamps to
    // detect reordering on its own.
    const patch = { [field]: value, updatedAt } as ClientSentimentPatch
    const prev = fieldChainsRef.current[field] ?? Promise.resolve()
    const chained = prev
      .catch(() => undefined)
      .then(() => update.mutateAsync({ clientSentimentPatch: patch }))
      .then(
        () => {
          if (seq === saveSeqRef.current) lastSavedRef.current = next
        },
        () => {
          queryClient.invalidateQueries(packetQueryKey)
        }
      )
    fieldChainsRef.current[field] = chained
  }

  const indicator = update.isLoading
    ? 'Saving…'
    : update.isError
    ? 'Save failed'
    : sameAsSaved(draft, lastSavedRef.current)
    ? hydrated
      ? 'Saved'
      : ''
    : 'Unsaved'

  return { draft, saveField, indicator }
}
